import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, AlertTriangle, ChevronDown } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import { elAPI, elErr } from '../../../lib/elAPI';

const sel = 'h-8 rounded-md border bg-background px-2 text-xs';
const blankProvider = { name: '', country: '', type: 'contractor', registration: '', dd_date: '', dd_status: '' };

export const NoCountriesBanner = ({ meta, onGo }) => (meta?.missing_countries ? (
  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="off-missing-countries">
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
    <span>Engagement letters can’t be sent yet. <button type="button" className="font-medium underline" onClick={onGo} data-testid="off-missing-link">Add your offshore countries in Offshore services</button>.</span>
  </div>) : null);

const Ticks = ({ label, options, value, onChange, tid }) => (
  <div className="space-y-1">
    <p className="text-xs text-muted-foreground">{label} (optional)</p>
    <div className="grid gap-1 sm:grid-cols-2">
      {options.map((o) => (
        <label key={o} className="flex items-center gap-2 text-sm">
          <Checkbox checked={value.includes(o)} onCheckedChange={(v) => onChange(v ? [...value, o] : value.filter((x) => x !== o))} data-testid={`${tid}-${o.replace(/\W+/g, '-').toLowerCase()}`} />{o}
        </label>))}
    </div>
  </div>
);

const ProviderRow = ({ p, i, types, set, remove }) => (
  <div className="grid gap-2 md:grid-cols-[1.5fr_1fr_1fr_auto]" data-testid={`off-provider-${i}`}>
    <Input className="h-8" placeholder="Name" value={p.name} onChange={(e) => set({ ...p, name: e.target.value })} data-testid={`off-provider-name-${i}`} />
    <Input className="h-8" placeholder="Country" value={p.country} onChange={(e) => set({ ...p, country: e.target.value })} data-testid={`off-provider-country-${i}`} />
    <select className={sel} value={p.type} onChange={(e) => set({ ...p, type: e.target.value })} data-testid={`off-provider-type-${i}`}>{Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={remove} data-testid={`off-provider-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
  </div>
);

const MoreDetails = ({ f, setF, meta }) => (
  <Collapsible>
    <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="off-more-toggle"><ChevronDown className="h-4 w-4" />More details (optional)</CollapsibleTrigger>
    <CollapsibleContent className="space-y-3 pt-3" data-testid="off-more">
      <label className="block space-y-1"><span className="text-xs text-muted-foreground">Access mode</span>
        <select className={`${sel} w-full`} value={f.access_mode} onChange={(e) => setF({ ...f, access_mode: e.target.value })} data-testid="off-access-mode"><option value="">Not specified</option>{Object.entries(meta.access_modes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
      <label className="block space-y-1"><span className="text-xs text-muted-foreground">Confidentiality agreements and security controls</span><Textarea rows={3} value={f.safeguards} onChange={(e) => setF({ ...f, safeguards: e.target.value })} data-testid="off-safeguards" /></label>
      {f.providers.map((p, i) => (
        <div key={i} className="grid gap-2 md:grid-cols-[1.2fr_1fr_1fr_1fr]" data-testid={`off-provider-more-${i}`}>
          <span className="self-center text-xs">{p.name || `Provider ${i + 1}`}</span>
          <Input className="h-8" placeholder="ABN / registration" value={p.registration} onChange={(e) => setF({ ...f, providers: f.providers.map((x, j) => (j === i ? { ...x, registration: e.target.value } : x)) })} />
          <Input className="h-8" type="date" title="Due-diligence date" value={p.dd_date} onChange={(e) => setF({ ...f, providers: f.providers.map((x, j) => (j === i ? { ...x, dd_date: e.target.value } : x)) })} />
          <select className={sel} value={p.dd_status} onChange={(e) => setF({ ...f, providers: f.providers.map((x, j) => (j === i ? { ...x, dd_status: e.target.value } : x)) })}>
            <option value="">Due diligence –</option><option value="in_progress">In progress</option><option value="completed">Completed</option></select>
        </div>))}
    </CollapsibleContent>
  </Collapsible>
);

const OffshoreForm = ({ meta, reload }) => {
  const base = meta.config || { countries: [], providers: [], service_categories: meta.service_options, info_categories: meta.info_options, access_mode: '', safeguards: '' };
  const [f, setF] = useState({ ...base, providers: (base.providers || []).map((p) => ({ ...blankProvider, ...p })) });
  const [countries, setCountries] = useState((base.countries || []).join(', '));
  const save = () => elAPI.saveOffshore({ ...f, countries: countries.split(',').map((x) => x.trim()).filter(Boolean) })
    .then((r) => { toast.success(r.material_changes.length ? 'Saved – owners notified, consented clients marked Disclosure required' : 'Saved'); reload(); })
    .catch((e) => toast.error(elErr(e)));
  return (
    <Card data-testid="off-config-card"><CardHeader className="pb-2"><CardTitle className="text-base">Offshore services</CardTitle></CardHeader>
      <CardContent className="space-y-4 text-sm">
        {!(base.countries || []).length && <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm" role="alert" data-testid="off-no-country-callout">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" /><span><span className="font-medium">No offshore country entered yet.</span> Engagement letters and addenda can’t be sent until you add at least one country below and save. Providers are optional.</span></div>}
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Countries where offshore work is performed (comma separated)</span>
          <Input value={countries} placeholder="e.g. Philippines, India" onChange={(e) => setCountries(e.target.value)} data-testid="off-countries" /></label>
        <div className="space-y-2">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">Providers (optional – if none, letters say offshore work is done by our own employees/contractors)</span>
            <Button size="sm" variant="outline" onClick={() => setF({ ...f, providers: [...f.providers, { ...blankProvider }] })} data-testid="off-provider-add"><Plus className="mr-1 h-3.5 w-3.5" />Add provider</Button></div>
          {f.providers.map((p, i) => <ProviderRow key={i} p={p} i={i} types={meta.provider_types} set={(np) => setF({ ...f, providers: f.providers.map((x, j) => (j === i ? np : x)) })} remove={() => setF({ ...f, providers: f.providers.filter((_, j) => j !== i) })} />)}
        </div>
        <Ticks label="Services performed offshore" options={meta.service_options} value={f.service_categories} onChange={(v) => setF({ ...f, service_categories: v })} tid="off-svc" />
        <Ticks label="Information offshore staff may access" options={meta.info_options} value={f.info_categories} onChange={(v) => setF({ ...f, info_categories: v })} tid="off-info" />
        <MoreDetails f={f} setF={setF} meta={meta} />
        <Button onClick={save} data-testid="off-save">Save</Button>
      </CardContent></Card>
  );
};

const AiRegisterCard = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { elAPI.aiProviders().then((r) => setRows(r.providers)).catch(() => {}); }, []);
  return (
    <Card data-testid="ai-register-card"><CardHeader className="pb-2"><CardTitle className="text-base">AI providers in use</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        {rows.map((r) => (
          <div key={r.id} className="flex items-start justify-between gap-2" data-testid={`ai-provider-${r.use_key}`}>
            <div><p className="font-medium">{r.name}</p><p className="text-xs text-muted-foreground">{r.purpose}</p></div>
            <Badge className={r.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-muted'} data-testid={`ai-provider-status-${r.use_key}`}>{r.status === 'approved' ? 'Active' : 'Inactive'}</Badge>
          </div>))}
      </CardContent></Card>
  );
};

const StaffOffshore = ({ meta, reload }) => {
  const providers = (meta.config?.providers || []).filter((p) => p.type !== 'direct_employee');
  const set = (s, v) => elAPI.setStaffOffshore(s.user_id, v === 'none' ? { kind: 'none' } : v === 'direct' ? { kind: 'direct' } : { kind: 'provider', provider_name: v })
    .then(() => { toast.success('Updated'); reload(); }).catch((e) => toast.error(elErr(e)));
  return (
    <Card data-testid="off-staff-card"><CardHeader className="pb-2"><CardTitle className="text-base">Offshore personnel</CardTitle></CardHeader>
      <CardContent className="space-y-1">
        <p className="pb-2 text-xs text-muted-foreground">Offshore staff can’t open, list or search clients marked “No offshore access”.</p>
        {meta.staff.map((s) => {
          const op = s.offshore_personnel;
          const v = !op ? 'none' : op.kind === 'direct' ? 'direct' : op.provider_name;
          return (
            <div key={s.user_id} className="flex items-center justify-between border-b py-1.5 text-sm last:border-0" data-testid={`off-staff-${s.user_id}`}>
              <span>{s.name || s.email} <span className="text-xs text-muted-foreground">· {s.role}</span></span>
              <select className={sel} value={v} onChange={(e) => set(s, e.target.value)} data-testid={`off-staff-select-${s.user_id}`}>
                <option value="none">Onshore</option><option value="direct">Offshore employee</option>
                {providers.map((p) => <option key={p.name} value={p.name}>Via {p.name}</option>)}
              </select>
            </div>);
        })}
      </CardContent></Card>
  );
};

export const OffshorePanel = ({ onSaved }) => {
  const [meta, setMeta] = useState(null);
  const load = useCallback(() => elAPI.offshore().then((m) => { setMeta(m); onSaved?.(m); }).catch((e) => toast.error(elErr(e))), [onSaved]);
  useEffect(() => { load(); }, [load]);
  if (!meta) return null;
  return (
    <div className="space-y-4" data-testid="el-offshore-panel">
      <OffshoreForm key={meta.version || 0} meta={meta} reload={load} />
      <div className="grid gap-4 lg:grid-cols-2"><StaffOffshore meta={meta} reload={load} /><AiRegisterCard /></div>
    </div>
  );
};
